import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import {
  UnifiedInvoice,
  UnifiedInvoiceFilters,
  CreateUnifiedInvoiceRequest,
  CreateUnifiedCreditNoteRequest,
  CreateUnifiedDownPaymentRequest,
  CreateFromTimesheetsRequest,
  CreateStandaloneInvoiceRequest,
  UpdateStatusRequest,
  AvailableForApplication,
  UnifiedApplication,
  EmailFormData,
  InvoiceRecipientsResponse,
  SplitPreview,
} from '@/types/invoicing';

/**
 * Attach uninvoiced timesheets/costs to an existing invoice.
 * Pure reconciliation by default; pass recalculateTotals=true (draft only)
 * to rebuild stored subtotal/VAT/total.
 */
export async function attachItemsToInvoice(
  invoiceId: number,
  payload: {
    timesheet_ids?: number[]
    cost_ids?: number[]
    recalculate_totals?: boolean
  },
) {
  const { data } = await api.post(
    `${ROUTES.API.INVOICING_UNIFIED}${invoiceId}/attach-items/`,
    payload,
  )
  return data
}

/**
 * Flag timesheets / costs as billed externally ("write-off"). They drop
 * out of WIP and no longer trigger drifting-timesheet anomalies.
 */
export async function writeOffItems(payload: {
  timesheet_ids?: number[]
  cost_ids?: number[]
  reason?: string
}): Promise<{ timesheets: number; costs: number }> {
  const { data } = await api.post(
    `${ROUTES.API.INVOICING_UNIFIED}write-off/`,
    payload,
  )
  return data
}

export class UnifiedInvoiceAPI {
  /**
   * Fetch all invoices with optional filters
   */
  static async getInvoices(filters?: UnifiedInvoiceFilters & { 
    limit?: number; 
    offset?: number; 
    search?: string;
    include_details?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());
    if (filters?.offset) queryParams.append('offset', filters.offset.toString());
    if (filters?.type) queryParams.append('type', filters.type);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.client) queryParams.append('client', filters.client.toString());
    if (filters?.matter) queryParams.append('matter', filters.matter.toString());
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.from_date) queryParams.append('from_date', filters.from_date);
    if (filters?.to_date) queryParams.append('to_date', filters.to_date);
    if (filters?.include_details) queryParams.append('include_details', 'true');
    if (filters?.ordering) queryParams.append('ordering', filters.ordering);
      
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get a single invoice by ID
   */
  static async getInvoice(id: number): Promise<UnifiedInvoice> {
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}${id}/`);
    return response.data;
  }

  /**
   * Create a new invoice (any type)
   */
  static async createInvoice(
    data: CreateUnifiedInvoiceRequest | CreateUnifiedCreditNoteRequest | CreateUnifiedDownPaymentRequest
  ): Promise<UnifiedInvoice> {
    const response = await api.post(ROUTES.API.INVOICING_UNIFIED, data);
    return response.data;
  }

  /**
   * Create invoice from timesheets using specialized endpoint
   */
  static async createFromTimesheets(data: CreateFromTimesheetsRequest): Promise<UnifiedInvoice> {
    const response = await api.post(`${ROUTES.API.INVOICING_UNIFIED}create_from_timesheets/`, data);
    return response.data;
  }

  /**
   * Create standalone invoice using specialized endpoint
   */
  static async createStandaloneInvoice(data: CreateStandaloneInvoiceRequest): Promise<UnifiedInvoice> {
    const response = await api.post(`${ROUTES.API.INVOICING_UNIFIED}create_standalone/`, data);
    return response.data;
  }

  /**
   * Create one invoice per client, merging selected matters.
   */
  static async createPerClient(data: {
    matters: Array<{ matter_id: number; timesheet_ids: number[]; cost_ids: number[] }>;
    issue_date?: string;
    due_days?: number;
    send_email_notification?: boolean;
    notes?: string;
  }): Promise<UnifiedInvoice[]> {
    const response = await api.post(
      `${ROUTES.API.INVOICING_UNIFIED}create_per_client/`,
      data,
    );
    return Array.isArray(response.data) ? response.data : [response.data];
  }

  /**
   * Preview fee-sharing split before creating invoices.
   * Returns per-client amounts, detail levels, and adjusted totals.
   */
  static async previewSplit(data: CreateFromTimesheetsRequest): Promise<SplitPreview> {
    const response = await api.post(`${ROUTES.API.INVOICING_UNIFIED}preview_split/`, data);
    return response.data;
  }

  /**
   * Update an existing invoice
   */
  static async updateInvoice(id: number, data: Partial<UnifiedInvoice>): Promise<UnifiedInvoice> {
    const response = await api.patch(`${ROUTES.API.INVOICING_UNIFIED}${id}/`, data);
    return response.data;
  }

  /**
   * Update invoice status using specialized endpoint
   */
  static async updateStatus(id: number, status: UpdateStatusRequest['status']): Promise<UnifiedInvoice> {
    const response = await api.post(`${ROUTES.API.INVOICING_UNIFIED}${id}/update_status/`, { status });
    return response.data;
  }

  /**
   * Delete an invoice
   */
  static async deleteInvoice(id: number): Promise<void> {
    await api.delete(`${ROUTES.API.INVOICING_UNIFIED}${id}/`);
  }

  /**
   * Generate PDF for an invoice
   */
  static async generatePDF(id: number): Promise<{ blob: Blob; filename?: string }> {
    // Use fetch instead of axios to properly access Content-Disposition header
    // Axios filters headers based on Access-Control-Expose-Headers
    const token = localStorage.getItem('accessToken');
    const baseURL = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
    const url = `${baseURL}${ROUTES.API.INVOICING_UNIFIED}${id}/pdf/`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition');
    let filename: string | undefined;
    
    if (contentDisposition) {
      // Match filename="RBLegal_20250595_SD_Luisenthal_SMWW.pdf" or filename=file.pdf
      const filenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }
    
    const blob = await response.blob();
    return { blob, filename };
  }

  /**
   * Download a professional PDF of this invoice's timesheets.
   */
  static async downloadTimesheets(id: number): Promise<{ blob: Blob; filename?: string }> {
    const token = localStorage.getItem('accessToken');
    const baseURL = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
    const url = `${baseURL}${ROUTES.API.INVOICING_UNIFIED}${id}/download_timesheets/`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentDisposition = response.headers.get('content-disposition');
    let filename: string | undefined;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].trim();
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }

  /**
   * Get PDF (with conversion if needed)
   */
  static async getPDF(id: number): Promise<Blob> {
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}${id}/get_pdf/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Download original document
   */
  static async downloadDocument(id: number): Promise<Blob> {
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}${id}/download_document/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Upload document for an invoice
   */
  static async uploadDocument(id: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('document', file);
    
    await api.post(`${ROUTES.API.INVOICING_UNIFIED}${id}/upload_document/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Regenerate document using firm template
   */
  static async regenerateDocument(id: number): Promise<void> {
    await api.post(`${ROUTES.API.INVOICING_UNIFIED}${id}/regenerate_document/`);
  }

  /**
   * Send an invoice via email
   */
  static async sendEmail(id: number, emailData: EmailFormData): Promise<void> {
    await api.post(`${ROUTES.API.INVOICING_UNIFIED}${id}/send_email/`, emailData);
  }

  /**
   * Get recipients for an invoice
   */
  static async getRecipients(id: number): Promise<InvoiceRecipientsResponse> {
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}${id}/recipients/`);
    return response.data;
  }

  /**
   * Get recipients for multiple invoices (for bulk operations)
   */
  static async getBulkRecipients(ids: number[]): Promise<InvoiceRecipientsResponse[]> {
    const promises = ids.map(id => this.getRecipients(id));
    return Promise.all(promises);
  }

  /**
   * Get applications for an invoice (credit notes/down payments applied)
   */
  static async getApplications(id: number): Promise<UnifiedApplication[]> {
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}${id}/applications/`);
    return response.data;
  }

  /**
   * Get available items that can be applied to an invoice
   */
  static async getAvailableForApplication(id: number): Promise<AvailableForApplication[]> {
    const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}${id}/available_for_application/`);
    return response.data;
  }

  /**
   * Apply a credit note or down payment to an invoice
   */
  static async applyToInvoice(
    sourceId: number, 
    targetInvoiceId: number, 
    amount?: number, 
    notes?: string
  ): Promise<void> {
    const data: any = { target_invoice_id: targetInvoiceId };
    if (amount !== undefined) data.amount = amount;
    if (notes) data.notes = notes;

    await api.post(`${ROUTES.API.INVOICING_UNIFIED}${sourceId}/apply_to_invoice/`, data);
  }

  /**
   * Generate document for an invoice (regenerate using template)
   */
  static async generateDocument(id: number): Promise<void> {
    await api.post(`${ROUTES.API.INVOICING_UNIFIED}${id}/regenerate_document/`);
  }

  /**
   * Merge multiple invoices for the same client into one.
   * The target (lowest invoice number) absorbs timesheets, costs,
   * and amounts from the other invoices. Source invoices are deleted
   * and subsequent invoice numbers are shifted down.
   */
  static async mergeInvoices(invoiceIds: number[]): Promise<UnifiedInvoice> {
    const response = await api.post(`${ROUTES.API.INVOICING_UNIFIED}merge/`, {
      invoice_ids: invoiceIds,
    });
    return response.data;
  }

  /**
   * Get totals for filtered invoices
   */
  static async getTotals(filters?: UnifiedInvoiceFilters & { search?: string }) {
    const queryParams = new URLSearchParams();
    
    if (filters?.type) queryParams.append('type', filters.type);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.client) queryParams.append('client', filters.client.toString());
    if (filters?.matter) queryParams.append('matter', filters.matter.toString());
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.from_date) queryParams.append('from_date', filters.from_date);
    if (filters?.to_date) queryParams.append('to_date', filters.to_date);

    try {
      // Use unified totals endpoint
      const response = await api.get(`${ROUTES.API.INVOICING_UNIFIED}totals/?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      // Fallback to legacy invoice totals endpoint
      const response = await api.get(`${ROUTES.API.INVOICES}totals/?${queryParams.toString()}`);
      return response.data;
    }
  }
}
